import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Send, MessageCircle } from 'lucide-react';

export default function Messages() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const { axiosInstance, user } = useAuth();

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await axiosInstance.get('/messages/conversations');
      setConversations(response.data);
      if (response.data.length > 0) {
        setSelectedConversation(response.data[0]);
      }
    } catch (error) {
      toast.error('Échec du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      await axiosInstance.post('/messages', {
        to_user_id: selectedConversation.other_user.id,
        content: newMessage
      });
      setNewMessage('');
      fetchConversations();
    } catch (error) {
      toast.error('Échec de l\'envoi');
    }
  };

  if (loading) {
    return (
      <Layout title="Messagerie">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Messagerie">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="messages-container">
        {/* Conversations List */}
        <Card className="glass-card border-amber-200 lg:col-span-1">
          <CardContent className="p-4">
            <h3 className="font-semibold text-amber-900 mb-4">Conversations</h3>
            <div className="space-y-2">
              {conversations.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">Aucune conversation</p>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.conversation_id}
                    onClick={() => setSelectedConversation(conv)}
                    className={`p-3 rounded-lg cursor-pointer transition ${
                      selectedConversation?.conversation_id === conv.conversation_id
                        ? 'bg-amber-100 border-2 border-amber-400'
                        : 'bg-white hover:bg-amber-50 border border-amber-200'
                    }`}
                    data-testid={`conversation-${conv.conversation_id}`}
                  >
                    <div className="flex items-center space-x-3">
                      {conv.other_user.profile_photo ? (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${conv.other_user.profile_photo}`} 
                          alt={conv.other_user.first_name}
                          className="w-10 h-10 rounded-full border-2 border-amber-400"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center">
                          <span className="text-sm font-bold text-amber-700">
                            {conv.other_user.first_name[0]}{conv.other_user.last_name[0]}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-amber-900 truncate">
                          {conv.other_user.first_name} {conv.other_user.last_name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {conv.messages[conv.messages.length - 1]?.content}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Messages Area */}
        <Card className="glass-card border-amber-200 lg:col-span-2">
          <CardContent className="p-4 flex flex-col" style={{ height: '600px' }}>
            {selectedConversation ? (
              <>
                {/* Header */}
                <div className="pb-4 border-b border-amber-200">
                  <div className="flex items-center space-x-3">
                    {selectedConversation.other_user.profile_photo ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${selectedConversation.other_user.profile_photo}`} 
                        alt={selectedConversation.other_user.first_name}
                        className="w-12 h-12 rounded-full border-2 border-amber-400"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-amber-200 flex items-center justify-center">
                        <span className="text-lg font-bold text-amber-700">
                          {selectedConversation.other_user.first_name[0]}{selectedConversation.other_user.last_name[0]}
                        </span>
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-amber-900">
                        {selectedConversation.other_user.first_name} {selectedConversation.other_user.last_name}
                      </h3>
                      <p className="text-xs text-gray-500">{selectedConversation.other_user.role}</p>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 py-4" data-testid="messages-scroll-area">
                  <div className="space-y-4">
                    {selectedConversation.messages.map((msg, idx) => {
                      const isMe = msg.from_user_id === user.id;
                      return (
                        <div
                          key={idx}
                          className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                          data-testid={`message-${idx}`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                              isMe
                                ? 'bg-gradient-to-r from-amber-400 to-yellow-400 text-gray-900'
                                : 'bg-white border border-amber-200 text-gray-800'
                            }`}
                          >
                            <p className="text-sm">{msg.content}</p>
                            <p className="text-xs mt-1 opacity-70">
                              {new Date(msg.timestamp).toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>

                {/* Input */}
                <form onSubmit={handleSendMessage} className="pt-4 border-t border-amber-200">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Écrire un message..."
                      className="flex-1 border-amber-300"
                      data-testid="message-input"
                    />
                    <Button type="submit" className="btn-gold" data-testid="send-message-button">
                      <Send size={20} />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageCircle size={48} className="mx-auto mb-4 text-amber-400" />
                  <p>Sélectionnez une conversation</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}